import React, { createContext, useContext, PropsWithChildren, useRef, useMemo } from 'react';
import isEqual from 'lodash-es/isEqual';
import { ko } from '../../locales/ko';
import { ModalProps } from '../modal';
import { PaginationProps } from '../pagination';
import { SectionProps } from '../../container/section';

export interface CommonComponentProps {
  modal?: Pick<ModalProps, 'fill' | 'dense'>;
  pagination?: Pick<PaginationProps, 'siblingCount' | 'hasArrows'> & {
    /** @deprecated hasArrows를 대신 사용해주세요. */
    showArrowButton?: PaginationProps['hasArrows'];
  };
  section?: Pick<SectionProps, 'responsive'>;
}

export interface ProviderConfig {
  locale?: typeof ko;
  components?: CommonComponentProps;
}

export const defaultProviderConfig: {
  current: ProviderConfig;
} = {
  current: {
    locale: ko,
  },
};

export function setContextConfig(config: ProviderConfig) {
  defaultProviderConfig.current = config;
}

export function getContextConfig() {
  return defaultProviderConfig.current;
}

export const providerContext = createContext<ProviderConfig | null>(null);
export type ProviderProps = ProviderConfig;

export const useProviderConfig = () => {
  return useContext(providerContext) ?? getContextConfig();
};

export const Provider = ({ children, ...props }: PropsWithChildren<ProviderProps>) => {
  const config: ProviderProps = { ...useProviderConfig(), ...props };
  const contextRef = useRef<ProviderProps>({});

  if (!isEqual(config, contextRef.current)) {
    contextRef.current = config;
  }

  const contextValue = useMemo(() => contextRef.current, [contextRef.current]);

  return <providerContext.Provider value={contextValue}>{children}</providerContext.Provider>;
};
